import { yupResolver } from '@hookform/resolvers/yup';
import {
    Accordion,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Grid,
    Typography,
} from '@mui/material';
import BaseInputNumber from 'common/components/base-input-number';
import FormProvider from 'common/components/form-provider';
import { servicePaymentSchema } from 'common/schema';
import React from 'react';
import { useForm } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BaseSwitch from 'common/components/base-switch';
import { TargetOption } from './target-option';
import { colors } from 'common/constants';
import { createStyleSheet, handleError } from 'utils/helpers';
import {
    listAddress,
    listGender,
    listAge,
    listMaritalStatus,
    listChildrens,
    listFields,
    listIncome,
    listJobs,
} from '../mock-data';

import { useNavigate, useParams } from 'react-router-dom';
import { CheckPricePayment, paymentDetail } from 'services/payment.service';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'common/hook';
import {
    setSurveyPaymentSelectObject,
    setCostInfo,
    setPaymentProduct,
} from 'slices/paymentSlice';
import { RootState } from 'plugins/store';

const BASE_SIZE = 8;
interface ISelectObjectProps {
    tabIndex: number;
    setActiveStep: (index: number) => void;
    sizeModifier?: number;
    disableForm?: boolean;
}
const SelectObject: React.FC<ISelectObjectProps> = ({
    setActiveStep,
    sizeModifier = 1,
    disableForm = false,
}) => {
    const one_times_base_size = BASE_SIZE * sizeModifier;
    const one_and_a_half_times_base_size = BASE_SIZE * 1.5 * sizeModifier;
    const two_times_base_size = BASE_SIZE * 2 * sizeModifier;
    const two_and_a_half_times_base_size = BASE_SIZE * 2.5 * sizeModifier;
    const three_times_base_size = BASE_SIZE * 3 * sizeModifier;
    const three_and_a_half_times_base_size = BASE_SIZE * 3.5 * sizeModifier;
    const four_times_base_size = BASE_SIZE * 4 * sizeModifier;
    const four_and_a_half_times_base_size = BASE_SIZE * 4.5 * sizeModifier;
    const five_times_base_size = BASE_SIZE * 5 * sizeModifier;
    const six_times_base_size = BASE_SIZE * 6 * sizeModifier;
    const six_and_a_half_times_base_size = BASE_SIZE * 6.5 * sizeModifier;
    const seven_and_a_half_times_base_size = BASE_SIZE * 7.5 * sizeModifier;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const surveyPayment = useAppSelector(
        (state: RootState) => state.payment.surveyPayment
    );
    const currentCampaign = useAppSelector(
        (state: RootState) => state.campaignState.campaign
    );
    const { id: campaignId } = useParams();
    const methods = useForm({
        resolver: yupResolver(servicePaymentSchema),
        defaultValues: {
            numberOfPeople: surveyPayment.costInfo.number_of_answer,
            isRegionIncluded: surveyPayment.payment_target.address.length > 0,
            isGenderIncluded: surveyPayment.payment_target.gender.length > 0,
            isAgeIncluded: surveyPayment.payment_target.dob.length > 0,
            isMaritalStatusIncluded:
                surveyPayment.payment_target.marriage.length > 0,
            isChildrenIncluded: surveyPayment.payment_target.child.length > 0,
            isFieldIncluded:
                surveyPayment.payment_target.career_field.length > 0,
            isJobIncluded: surveyPayment.payment_target.career.length > 0,
            isPersonalIncomeIncluded:
                surveyPayment.payment_target.monthly_personal_income.length > 0,
            isFamilyIncomeIncluded:
                surveyPayment.payment_target.monthly_family_income.length > 0,
            dob: surveyPayment.payment_target.dob,
            gender: surveyPayment.payment_target.gender,
            address: surveyPayment.payment_target.address,
            marriage: surveyPayment.payment_target.marriage,
            child: surveyPayment.payment_target.child,
            career: surveyPayment.payment_target.career,
            career_field: surveyPayment.payment_target.career_field,
            monthly_personal_income:
                surveyPayment.payment_target.monthly_personal_income,
            monthly_family_income:
                surveyPayment.payment_target.monthly_family_income,
        },
        mode: 'onChange',
    });
    const { handleSubmit, watch, reset } = methods;
    const onSubmit = handleSubmit(async (data) => {
        if (disableForm) return;
        try {
            const payment_target_payload = {
                dob: data.dob,
                gender: data.gender,
                address: data.address,
                marriage: data.marriage,
                child: data.child,
                career: data.career,
                career_field: data.career_field,
                monthly_personal_income: data.monthly_personal_income,
                monthly_family_income: data.monthly_family_income,
            };
            const payload = {
                payment: {
                    product_type: 'Survey',
                    product_id: campaignId,
                    total_answer: data.numberOfPeople,
                    payment_target: payment_target_payload,
                },
            };
            const response = await CheckPricePayment(payload);
            if (response.success) {
                const responseAttributes = response.data.payment.attributes;
                if (responseAttributes) {
                    dispatch(
                        setCostInfo({
                            total_price: responseAttributes.total_price,
                            number_of_question:
                                responseAttributes.total_question,
                            number_of_answer: responseAttributes.total_answer,
                            base_price: responseAttributes.price_unit,
                            discount: responseAttributes.discount,
                            tax: responseAttributes.tax,
                            date_payment: responseAttributes.created_at_d_m,
                        })
                    );
                }

                dispatch(setSurveyPaymentSelectObject(payment_target_payload));
                setActiveStep(1);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            handleError(error);
        }
    });

    const fetchPaymentDetail = React.useCallback(async () => {
        if (!currentCampaign) return;
        try {
            const response = await paymentDetail(
                currentCampaign.paymentStatus.id
            );
            if (response.success) {
                const responseAttributes = response.data.payment.attributes;
                if (responseAttributes) {
                    const payment_target_payload: {
                        dob: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        gender: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        address: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        marriage: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        child: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        career: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        career_field: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        monthly_personal_income: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                        monthly_family_income: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
                    } = {
                        dob: [],
                        gender: [],
                        address: [],
                        marriage: [],
                        child: [],
                        career: [],
                        career_field: [],
                        monthly_personal_income: [],
                        monthly_family_income: [],
                    };

                    responseAttributes.payment_details.forEach(
                        (item: {
                            attributes: {
                                service: string;
                                field_name: string;
                                field_value: string;
                            };
                        }) => {
                            if (item.attributes.service === 'target') {
                                const fieldName = item.attributes.field_name;
                                const fieldValue = item.attributes.field_value;
                                if (
                                    fieldName === 'dob' ||
                                    fieldName === 'monthly_personal_income' ||
                                    fieldName === 'monthly_family_income'
                                ) {
                                    payment_target_payload[
                                        fieldName as keyof typeof payment_target_payload
                                    ].push(fieldValue);
                                } else {
                                    payment_target_payload[
                                        fieldName as keyof typeof payment_target_payload
                                    ].push(Number(fieldValue));
                                }
                            }
                        }
                    );
                    reset({
                        numberOfPeople: responseAttributes.total_answer,
                        isRegionIncluded:
                            payment_target_payload.address.length > 0,
                        isGenderIncluded:
                            payment_target_payload.gender.length > 0,
                        isAgeIncluded: payment_target_payload.dob.length > 0,
                        isMaritalStatusIncluded:
                            payment_target_payload.marriage.length > 0,
                        isChildrenIncluded:
                            payment_target_payload.child.length > 0,
                        isFieldIncluded:
                            payment_target_payload.career_field.length > 0,
                        isJobIncluded: payment_target_payload.career.length > 0,
                        isPersonalIncomeIncluded:
                            payment_target_payload.monthly_personal_income
                                .length > 0,
                        isFamilyIncomeIncluded:
                            payment_target_payload.monthly_family_income
                                .length > 0,
                        dob: payment_target_payload.dob,
                        gender: payment_target_payload.gender,
                        address: payment_target_payload.address,
                        marriage: payment_target_payload.marriage,
                        child: payment_target_payload.child,
                        career: payment_target_payload.career,
                        career_field: payment_target_payload.career_field,
                        monthly_personal_income:
                            payment_target_payload.monthly_personal_income,
                        monthly_family_income:
                            payment_target_payload.monthly_family_income,
                    });
                    dispatch(
                        setCostInfo({
                            total_price: responseAttributes.total_price,
                            number_of_question:
                                responseAttributes.total_question,
                            number_of_answer: responseAttributes.total_answer,
                            base_price: responseAttributes.price_unit,
                            discount: responseAttributes.discount,
                            tax: responseAttributes.tax,
                            date_payment: responseAttributes.created_at_d_m,
                        })
                    );
                    dispatch(
                        setPaymentProduct({
                            product_type: responseAttributes.product_type,
                            product_id: responseAttributes.product_id,
                        })
                    );
                }
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            handleError(error);
        }
    }, [currentCampaign, reset, dispatch]);

    React.useEffect(() => {
        dispatch(
            setPaymentProduct({
                product_type: 'Survey',
                product_id: campaignId,
            })
        );
        if (!currentCampaign) return;
        if (currentCampaign.paymentStatus.status === 'paid') {
            fetchPaymentDetail();
        }
    }, [campaignId, dispatch, currentCampaign, fetchPaymentDetail]);

    const styles = createStyleSheet({
        title: {
            fontWeight: 600,
            fontSize: four_times_base_size,
            lineHeight: `${four_and_a_half_times_base_size}px`,
        },
        switch: {
            transform: 'scale(1.5)',
            '& .MuiSwitch-thumb': {
                backgroundColor: 'var(--color-primary-light)',
            },
            '& .MuiSwitch-track': {
                backgroundColor: '#0000001A',
                opacity: 1,
            },
            '& .Mui-checked': {
                '& .MuiSwitch-thumb': {
                    backgroundColor: 'var(--color-primary-light)',
                },
                '& + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                    opacity: '1 !important',
                },
            },
        },
        accordion: {
            padding: 0,
            backgroundColor: 'unset',
            boxShadow: 'unset',
            border: 'unset',
            '&:before': {
                display: 'none',
            },
            title: {
                fontWeight: 600,
                fontSize: four_times_base_size,
                lineHeight: `${four_and_a_half_times_base_size}px`,
            },
        },
        accordionSummary: {
            padding: 0,
            '& .MuiAccordionSummary-content': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            },
        },
    });

    const renderExpandIcon = (open: boolean) => {
        return (
            <>
                <ExpandMoreIcon
                    sx={{
                        fontSize: six_times_base_size,
                        marginRight: `${one_times_base_size}px`,
                        transition: 'transform 0.3s ease',
                        transform: open ? 'rotate(0)' : 'rotate(-90deg)',
                    }}
                />
            </>
        );
    };
    return (
        <Box sx={{ marginTop: `${five_times_base_size}px` }}>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={11}>
                    <Grid item xs={7}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                marginBottom: `${three_times_base_size}px`,
                            }}
                        >
                            <Typography variant="h5" sx={styles.title}>
                                Số người cần khảo sát này
                            </Typography>
                            <BaseInputNumber
                                name="numberOfPeople"
                                sx={{
                                    width: '200px',
                                    '& .MuiInputBase-root': {
                                        fontSize: three_times_base_size,
                                        lineHeight: `${three_and_a_half_times_base_size}px`,
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        borderRadius: '0px',
                                        borderWidth: '2px',
                                    },
                                }}
                                disabled={disableForm}
                            />
                        </Box>
                        <Box marginTop={`${five_times_base_size}px`}>
                            <Accordion
                                disableGutters
                                sx={styles.accordion}
                                expanded={watch('isRegionIncluded')}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isRegionIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Khu vực
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isRegionIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box marginTop={`${two_times_base_size}px`}>
                                    <TargetOption
                                        name={'address'}
                                        showOnlyImage={true}
                                        itemsPerRow={3}
                                        options={listAddress}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                sx={styles.accordion}
                                expanded={watch('isGenderIncluded')}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isGenderIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Giới tính
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isGenderIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box marginTop={`${two_times_base_size}px`}>
                                    <TargetOption
                                        name={'gender'}
                                        showOnlyImage={true}
                                        options={listGender}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                sx={styles.accordion}
                                expanded={watch('isAgeIncluded')}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isAgeIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Độ tuổi
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isAgeIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'dob'}
                                        itemsPerRow={2}
                                        options={listAge}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isMaritalStatusIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isMaritalStatusIncluded') ??
                                                false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Tình trạng hôn nhân
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isMaritalStatusIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'marriage'}
                                        options={listMaritalStatus}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isChildrenIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isChildrenIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Con cái
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isChildrenIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'child'}
                                        options={listChildrens}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isFieldIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isFieldIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Lĩnh vực
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isFieldIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'career_field'}
                                        itemsPerRow={2}
                                        options={listFields}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isJobIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isJobIncluded') ?? false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Nghề nghiệp
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isJobIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'career'}
                                        itemsPerRow={2}
                                        options={listJobs}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isPersonalIncomeIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isPersonalIncomeIncluded') ??
                                                false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Thu nhập cá nhân
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isPersonalIncomeIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'monthly_personal_income'}
                                        options={listIncome}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                            <Accordion
                                disableGutters
                                expanded={watch('isFamilyIncomeIncluded')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    sx={styles.accordionSummary}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {renderExpandIcon(
                                            watch('isFamilyIncomeIncluded') ??
                                                false
                                        )}
                                        <Typography
                                            component="span"
                                            sx={styles.accordion.title}
                                        >
                                            Thu nhập gia đình
                                        </Typography>
                                    </Box>
                                    <BaseSwitch
                                        name="isFamilyIncomeIncluded"
                                        size="medium"
                                        sx={styles.switch}
                                    />
                                </AccordionSummary>
                                <Box>
                                    <TargetOption
                                        name={'monthly_family_income'}
                                        options={listIncome}
                                        sizeModifier={sizeModifier}
                                        disabled={disableForm}
                                    />
                                </Box>
                            </Accordion>
                        </Box>
                    </Grid>
                    <Grid item xs={5}>
                        {!disableForm && (
                            <>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: six_times_base_size,
                                        lineHeight: `${seven_and_a_half_times_base_size}px`,
                                    }}
                                >
                                    Hóa đơn
                                </Typography>
                                <Divider
                                    sx={{
                                        borderTop: '2px dashed #000',
                                        margin: `${two_and_a_half_times_base_size}px 0px ${six_times_base_size}px 0px`,
                                    }}
                                />
                            </>
                        )}
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: six_times_base_size,
                                fontWeight: 600,
                                lineHeight: `${four_times_base_size}px`,
                                marginBottom: `${one_and_a_half_times_base_size}px`,
                            }}
                        >
                            {surveyPayment.costInfo.total_price_text}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: three_times_base_size,
                                lineHeight: `${four_times_base_size}px`,
                                fontWeight: 400,
                                marginBottom: `${five_times_base_size}px`,
                            }}
                        >
                            Tổng chi phí VND
                        </Typography>
                        {!disableForm && (
                            <Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{
                                        width: '100%',
                                        marginBottom: `${five_times_base_size}px`,
                                        minHeight:
                                            seven_and_a_half_times_base_size,
                                        borderRadius:
                                            seven_and_a_half_times_base_size,
                                        fontSize: three_times_base_size,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                    }}
                                >
                                    Thanh toán
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        width: '100%',
                                        marginBottom: `${seven_and_a_half_times_base_size}px`,
                                        minHeight:
                                            seven_and_a_half_times_base_size,
                                        borderRadius:
                                            seven_and_a_half_times_base_size,
                                        fontSize: three_times_base_size,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        backgroundColor: '#FF7916',
                                        '&:hover': {
                                            backgroundColor: '#FF7916',
                                        },
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/admin/campaign/data-collect/${campaignId}`
                                        )
                                    }
                                >
                                    Quay lại khảo sát
                                </Button>
                            </Box>
                        )}
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: four_times_base_size,
                                    fontWeight: 600,
                                    lineHeight: `${four_and_a_half_times_base_size}px`,
                                    marginBottom: `${one_and_a_half_times_base_size}px`,
                                }}
                            >
                                Ngày hoàn thành
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: three_times_base_size,
                                    fontWeight: 400,
                                    lineHeight: `${three_and_a_half_times_base_size}px`,
                                    marginBottom: `${seven_and_a_half_times_base_size}px`,
                                    color: '#000000B2',
                                }}
                            >
                                {surveyPayment.costInfo.date_payment}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 2,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: four_times_base_size,
                                    fontWeight: 600,
                                    lineHeight: `${four_and_a_half_times_base_size}px`,
                                }}
                            >
                                Chi tiết
                            </Typography>
                            <Box />

                            {[
                                {
                                    label: 'Số lượng câu hỏi',
                                    value: surveyPayment.costInfo
                                        .number_of_question,
                                    icon: '1',
                                },
                                {
                                    label: 'Chi phí một câu hỏi',
                                    value: `${surveyPayment.costInfo.base_price_text} VND`,
                                    icon: '2',
                                },
                                {
                                    label: 'Số người tham gia',
                                    value: surveyPayment.costInfo
                                        .number_of_answer,
                                    icon: '3',
                                },
                            ].map((item, index) => (
                                <React.Fragment key={index}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: three_times_base_size,
                                            fontWeight: 400,
                                            lineHeight: `${three_and_a_half_times_base_size}px`,
                                            color: '#000000B2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        {item.label}
                                        {item.icon && (
                                            <Box
                                                sx={{
                                                    width: `${two_and_a_half_times_base_size}px`,
                                                    height: `${two_and_a_half_times_base_size}px`,
                                                    backgroundColor:
                                                        colors.main,
                                                    fontSize:
                                                        one_and_a_half_times_base_size,
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    fontWeight: 700,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: three_times_base_size,
                                            fontWeight: 400,
                                            lineHeight: `${three_and_a_half_times_base_size}px`,
                                            textAlign: 'right',
                                            color: '#000000B2',
                                        }}
                                    >
                                        {item.value}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>

                        <Divider
                            sx={{
                                borderTop: '2px dashed #000',
                                margin: `${two_and_a_half_times_base_size}px 0px ${six_and_a_half_times_base_size}px 0px`,
                            }}
                        />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 2,
                            }}
                        >
                            <Box></Box>
                            <Box
                                sx={{
                                    fontSize: three_times_base_size,
                                    fontWeight: 400,
                                    lineHeight: `${three_and_a_half_times_base_size}px`,
                                    color: '#000000B2',
                                    textAlign: 'right',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                }}
                            >
                                Chi phí =
                                {[1, 2, 3].map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            key={index}
                                            sx={{
                                                width: `${two_and_a_half_times_base_size}px`,
                                                height: `${two_and_a_half_times_base_size}px`,
                                                backgroundColor: colors.main,
                                                fontSize:
                                                    one_and_a_half_times_base_size,
                                                color: '#fff',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {item}
                                        </Box>
                                        {index < [1, 2, 3].length - 1 && (
                                            <span
                                                style={{
                                                    fontSize:
                                                        one_and_a_half_times_base_size,
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {' '}
                                                X
                                            </span>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {[
                                {
                                    label: 'Tổng tiền',
                                    value: `${surveyPayment.costInfo.price_before_discount_text} VND`,
                                },
                                {
                                    label: 'Chiết khấu',
                                    value: `${surveyPayment.costInfo.discount_text} VND`,
                                },
                                {
                                    label: 'Tổng chi phí trước thuế',
                                    value: `${surveyPayment.costInfo.price_before_tax_text} VND`,
                                },
                                {
                                    label: 'VAT (10%)',
                                    value: `${surveyPayment.costInfo.tax_text} VND`,
                                },
                            ].map((item, index) => (
                                <React.Fragment key={index}>
                                    <Box
                                        sx={{
                                            fontSize: three_times_base_size,
                                            fontWeight: 400,
                                            lineHeight: `${three_and_a_half_times_base_size}px`,
                                            color: '#000000B2',
                                        }}
                                    >
                                        {item.label}
                                    </Box>
                                    <Box
                                        sx={{
                                            fontSize: three_times_base_size,
                                            fontWeight: 400,
                                            lineHeight: `${three_and_a_half_times_base_size}px`,
                                            color: '#000000B2',
                                            textAlign: 'right',
                                        }}
                                    >
                                        {item.value}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </FormProvider>
        </Box>
    );
};
export default SelectObject;
